import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
  View,
  Alert,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  RefreshControl,
  AppState,
  AppStateStatus,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {RouteProp} from '@react-navigation/native';
import {Header} from '../components/Header';
import {MatchHeader} from '../components/diretta/MatchHeader';
import {CommentaryTab} from '../components/diretta/CommentaryTab';
import {SummaryTab} from '../components/diretta/SummaryTab';
import {StatsTab} from '../components/diretta/StatsTab';
import {LineupsTab} from '../components/diretta/LineupsTab';
import {ChatTab} from '../components/diretta/ChatTab';
import {Icon} from '../components/Icon';
import {BackToTopButton} from '../components/BackToTopButton';
import {
  fetchCommentaries,
  fetchSummary,
  fetchStats,
  fetchLineups,
  fetchPlayerIncidents,
  fetchChatMessages,
  fetchNewChatMessages,
  checkGCSSignal,
  checkStatsGCSSignal,
  sendChatMessage,
  SummaryIncident,
  StatsData,
  PlayerIncidents,
} from '../services/diretta';
import {Match, Commentary, Lineup, ChatMessage} from '../types/diretta';
import {Colors} from '../theme/colors';
import {useAuth} from '../hooks/useAuth';
import {useTheme} from '../hooks/useTheme';
import {getStoredToken} from '../services/auth';

type RootStackParamList = {
  Home: undefined;
  Diretta: {match: Match};
  Article: any;
  Login: undefined;
};

type DirettaScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Diretta'>;
  route: RouteProp<RootStackParamList, 'Diretta'>;
};

type TabKey = 'live' | 'riassunto' | 'formazione' | 'stats';

const TABS: {key: TabKey; label: string}[] = [
  {key: 'live', label: 'Live'},
  {key: 'riassunto', label: 'Riassunto'},
  {key: 'formazione', label: 'Formazione'},
  {key: 'stats', label: 'Stats'},
];

export const DirettaScreen: React.FC<DirettaScreenProps> = ({navigation, route}) => {
  const {match} = route.params;
  const insets = useSafeAreaInsets();
  const {isAuthenticated, logout} = useAuth();
  const {colors, isDark} = useTheme();

  const [activeTab, setActiveTab] = useState<TabKey>('live');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chatModalVisible, setChatModalVisible] = useState(false);
  const [chatStatus, setChatStatus] = useState<string>('not_started');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);
  const SCROLL_THRESHOLD = 300;

  // Track scroll position for back-to-top button
  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    setShowBackToTop(offsetY > SCROLL_THRESHOLD);
  }, []);

  const handleBackToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo({y: 0, animated: true});
  }, []);

  // Data state
  const [commentaries, setCommentaries] = useState<Commentary[]>([]);
  const [summary, setSummary] = useState<Record<string, SummaryIncident[]>>({});
  const [stats, setStats] = useState<StatsData | null>(null);
  const [_lineups, setLineups] = useState<Lineup | null>(null);
  const [playerIncidents, setPlayerIncidents] = useState<PlayerIncidents>({});
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [chatPage, setChatPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [formazioneLoading, setFormazioneLoading] = useState(false);

  // Polling state
  const lastGCSTimestamp = useRef<number>(0);
  const lastStatsGCSTimestamp = useRef<number>(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastChatMessageId = useRef<number>(0);
  const lastSeenMessageCount = useRef<number>(0);
  const appState = useRef(AppState.currentState);
  const formazioneLoaded = useRef<boolean>(false);

  // Load initial data (commentary + chat only — others lazy-loaded on tab activation)
  const loadData = useCallback(async () => {
    setLoading(true);
    
    try {
      const [commData, chatResult, gcsSignal] = await Promise.all([
        fetchCommentaries(match.match_id),
        fetchChatMessages(match.match_id),
        checkGCSSignal(match.match_id),
      ]);

      setCommentaries(commData);
      setChatMessages(chatResult.messages);
      setChatStatus(chatResult.chatStatus);
      setHasMoreMessages(chatResult.hasMore);
      setChatPage(1);
      
      // Track last chat message ID for polling
      if (chatResult.messages.length > 0) {
        lastChatMessageId.current = Math.max(...chatResult.messages.map(m => m.id));
      }
      
      // Set initial GCS timestamp to prevent unnecessary first fetch
      if (gcsSignal?.updated) {
        lastGCSTimestamp.current = gcsSignal.updated;
        console.log('Initial GCS timestamp:', gcsSignal.updated);
      }
    } catch (error) {
      console.error('Error loading diretta data:', error);
    } finally {
      setLoading(false);
    }
  }, [match.match_id]);

  // 5-second chat polling
  const pollChatMessages = useCallback(async () => {
    if (lastChatMessageId.current === 0) return;
    
    const result = await fetchNewChatMessages(match.match_id, lastChatMessageId.current);
    setChatStatus(result.chatStatus);

    // Remove deleted messages
    if (result.deletedIds && result.deletedIds.length > 0) {
      setChatMessages(prev => prev.filter(msg => !result.deletedIds.includes(msg.id)));
    }

    if (result.messages.length > 0) {
      setChatMessages(prev => {
        const combined = [...result.messages, ...prev];
        // Remove duplicates
        const unique = combined.filter((msg, idx, arr) => 
          arr.findIndex(m => m.id === msg.id) === idx
        );
        return unique;
      });
      lastChatMessageId.current = Math.max(...result.messages.map(m => m.id));
    }
  }, [match.match_id]);

  // Start chat polling when chat modal is visible
  useEffect(() => {
    if (chatModalVisible) {
      // Poll immediately
      pollChatMessages();
      // Then every 5 seconds
      chatPollIntervalRef.current = setInterval(pollChatMessages, 5000);
    } else {
      if (chatPollIntervalRef.current) {
        clearInterval(chatPollIntervalRef.current);
        chatPollIntervalRef.current = null;
      }
    }
    
    return () => {
      if (chatPollIntervalRef.current) {
        clearInterval(chatPollIntervalRef.current);
      }
    };
  }, [chatModalVisible, pollChatMessages]);

  // Refresh commentary only (for GCS-triggered updates)
  const refreshCommentary = useCallback(async () => {
    const data = await fetchCommentaries(match.match_id);
    setCommentaries(data);
  }, [match.match_id]);

  // Fetch summary from server (GET only, no POST refresh)
  const doFetchSummary = useCallback(async () => {
    console.log('Fetching summary from server...');
    const data = await fetchSummary(match.match_id);
    setSummary(data);
  }, [match.match_id]);

  // Fetch stats from server
  const doFetchStats = useCallback(async () => {
    console.log('Fetching stats from server...');
    const data = await fetchStats(match.match_id);
    setStats(data);
  }, [match.match_id]);

  // Lazy-load formazione (lineups + player incidents) — only on first tab visit
  const loadFormazione = useCallback(async () => {
    if (formazioneLoaded.current) return;
    setFormazioneLoading(true);
    console.log('Loading formazione (lazy)...');
    const [lineupsData, incidentsData] = await Promise.all([
      fetchLineups(match.match_id),
      fetchPlayerIncidents(match.match_id),
    ]);
    setLineups(lineupsData);
    setPlayerIncidents(incidentsData);
    formazioneLoaded.current = true;
    setFormazioneLoading(false);
  }, [match.match_id]);

  // Check GCS signal for commentary updates
  const checkForUpdates = useCallback(async () => {
    const signal = await checkGCSSignal(match.match_id);
    
    if (signal && signal.updated > lastGCSTimestamp.current) {
      console.log('GCS signal changed, fetching commentary...');
      lastGCSTimestamp.current = signal.updated;
      await refreshCommentary();
    }
  }, [match.match_id, refreshCommentary]);


  // Check GCS signal for stats updates
  const checkForStatsUpdates = useCallback(async () => {
    const signal = await checkStatsGCSSignal(match.match_id);
    
    if (signal && signal.updated > lastStatsGCSTimestamp.current) {
      console.log('Stats GCS signal changed, fetching...');
      lastStatsGCSTimestamp.current = signal.updated;
      await doFetchStats();
    }
  }, [match.match_id, doFetchStats]);

  // Random delay for anti-thundering herd
  const randomDelay = (minMs: number, maxMs: number) => {
    return minMs + Math.floor(Math.random() * (maxMs - minMs));
  };

  // Commentary GCS polling (for Live tab)
  const startCommentaryPolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    const interval = randomDelay(30000, 45000); // 30-45 seconds
    console.log(`Starting commentary GCS polling every ~${Math.round(interval / 1000)}s`);
    
    pollIntervalRef.current = setInterval(checkForUpdates, interval);
  }, [checkForUpdates]);

  const stopCommentaryPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Summary GCS polling (for Riassunto tab)
  const summaryPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startSummaryPolling = useCallback(() => {
    if (summaryPollRef.current) return;

    console.log('Starting summary polling every 4 minutes');
    summaryPollRef.current = setInterval(doFetchSummary, 240000); // 4 minutes
  }, [doFetchSummary]);

  const stopSummaryPolling = useCallback(() => {
    if (summaryPollRef.current) {
      clearInterval(summaryPollRef.current);
      summaryPollRef.current = null;
    }
  }, []);

  // Stats GCS polling (for Stats tab)
  const statsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startStatsPolling = useCallback(() => {
    if (statsPollRef.current) return;

    const interval = 90000; // 90 seconds (matches website)
    console.log(`Starting stats GCS polling every 90s`);
    
    statsPollRef.current = setInterval(checkForStatsUpdates, interval);
  }, [checkForStatsUpdates]);

  const stopStatsPolling = useCallback(() => {
    if (statsPollRef.current) {
      clearInterval(statsPollRef.current);
      statsPollRef.current = null;
    }
  }, []);

  // Track previous tab to avoid double-refresh
  const prevTabRef = useRef<TabKey>(activeTab);

  // Handle tab change - start/stop polling based on active tab
  useEffect(() => {
    // Skip if tab hasn't actually changed
    if (prevTabRef.current === activeTab) return;
    prevTabRef.current = activeTab;

    // Stop all polling first
    stopCommentaryPolling();
    stopSummaryPolling();
    stopStatsPolling();

    if (activeTab === 'live') {
      startCommentaryPolling();
    } else if (activeTab === 'riassunto') {
      // Fetch immediately, then poll every 4 minutes
      doFetchSummary();
      startSummaryPolling();
    } else if (activeTab === 'stats') {
      // GCS check immediately, then start polling
      checkForStatsUpdates();
      startStatsPolling();
    } else if (activeTab === 'formazione') {
      // Lazy-load lineups on first visit
      loadFormazione();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCommentaryPolling();
      stopSummaryPolling();
      stopStatsPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle app state changes
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
        // App came to foreground - restart polling for active tab
        if (activeTab === 'live') {
          checkForUpdates();
          startCommentaryPolling();
        } else if (activeTab === 'riassunto') {
          doFetchSummary();
          startSummaryPolling();
        } else if (activeTab === 'stats') {
          checkForStatsUpdates();
          startStatsPolling();
        }
        // Restart chat polling if chat modal is open
        if (chatModalVisible) {
          pollChatMessages(); // Immediately fetch missed messages
          if (!chatPollIntervalRef.current) {
            chatPollIntervalRef.current = setInterval(pollChatMessages, 5000);
          }
        }
      } else if (nextAppState.match(/inactive|background/)) {
        // App went to background — stop all polling
        stopCommentaryPolling();
        stopSummaryPolling();
        stopStatsPolling();
        // Stop chat polling too
        if (chatPollIntervalRef.current) {
          clearInterval(chatPollIntervalRef.current);
          chatPollIntervalRef.current = null;
        }
      }
      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [activeTab, chatModalVisible, checkForUpdates, startCommentaryPolling, stopCommentaryPolling, doFetchSummary, startSummaryPolling, stopSummaryPolling, checkForStatsUpdates, startStatsPolling, stopStatsPolling, pollChatMessages]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Track unread messages
  useEffect(() => {
    const newCount = chatMessages.length - lastSeenMessageCount.current;
    if (!chatModalVisible && newCount > 0) {
      setUnreadCount(newCount);
    }
  }, [chatMessages.length, chatModalVisible]);

  // Reset unread when modal opens
  useEffect(() => {
    if (chatModalVisible) {
      lastSeenMessageCount.current = chatMessages.length;
      setUnreadCount(0);
    }
  }, [chatModalVisible, chatMessages.length]);

  // Pull to refresh
  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Send chat message
  const handleSendMessage = async (message: string) => {
    if (!isAuthenticated) return;
    
    const token = await getStoredToken();
    if (!token) return;
    
    const result = await sendChatMessage(match.match_id, message, token);
    if (result.success) {
      // Refresh chat
      const chatResult = await fetchChatMessages(match.match_id);
      setChatMessages(chatResult.messages);
      setChatStatus(chatResult.chatStatus);
    } else {
      // Handle force_exit: user has been banned or deleted
      if (result.action === 'force_exit') {
        setChatModalVisible(false);
        Alert.alert(
          'Accesso negato',
          result.errorMessage || 'Non puoi più accedere alla chat.',
          [{
            text: 'OK',
            onPress: async () => {
              await logout();
            },
          }],
        );
        return;
      }
      if (result.chatStatus === 'finished') {
        setChatStatus('finished');
      }
      Alert.alert('Errore', result.errorMessage || 'Impossibile inviare il messaggio');
    }
  };

  // Load older messages (infinite scroll)
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || loadingMore) return;
    setLoadingMore(true);
    const nextPage = chatPage + 1;
    const result = await fetchChatMessages(match.match_id, nextPage);
    if (result.messages.length > 0) {
      setChatMessages(prev => {
        const combined = [...prev, ...result.messages];
        const unique = combined.filter((msg, idx, arr) =>
          arr.findIndex(m => m.id === msg.id) === idx
        );
        return unique;
      });
      setChatPage(nextPage);
      setHasMoreMessages(result.hasMore);
    }
    setLoadingMore(false);
  }, [match.match_id, chatPage, hasMoreMessages, loadingMore]);

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'live':
        return <CommentaryTab commentaries={commentaries} loading={loading} />;
      case 'riassunto':
        return <SummaryTab summary={summary} loading={loading} />;
      case 'formazione':
        return (
          <LineupsTab
            lineups={_lineups}
            homeTeam={match.home_team.name}
            awayTeam={match.away_team.name}
            loading={formazioneLoading}
            playerIncidents={playerIncidents}
          />
        );
      case 'stats':
        return (
          <StatsTab
            stats={stats}
            homeTeam={match.home_team.name}
            awayTeam={match.away_team.name}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <View style={[styles.container, {backgroundColor: colors.background}]}>
      <Header showActionBar onBackPress={() => navigation.goBack()} />
      
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        onScroll={handleScroll}
        scrollEventThrottle={16}
        stickyHeaderIndices={[1]}
      >
        {/* Match header */}
        <MatchHeader match={match} />

        {/* Tab bar */}
        <View style={[styles.tabBar, {backgroundColor: colors.surface, borderBottomColor: colors.border}]}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabsContainer}
          >
            {TABS.map((tab) => (
              <TouchableOpacity
                key={tab.key}
                style={[styles.tab, activeTab === tab.key && styles.activeTab]}
                onPress={() => setActiveTab(tab.key)}
              >
                <Text style={[styles.tabText, {color: colors.textSecondary}, activeTab === tab.key && {color: colors.primary, fontWeight: '600'}]}>
                {tab.label}
              </Text>
                {activeTab === tab.key && <View style={styles.tabIndicator} />}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Tab content */}
        <View style={[styles.tabContent, {paddingBottom: insets.bottom + 80}]}>
          {renderTabContent()}
        </View>
      </ScrollView>

      {/* Fixed Footer Chat Button */}
      <View style={[styles.fixedFooter, {paddingBottom: insets.bottom || 20, backgroundColor: colors.background, borderTopColor: colors.border}]}>
        <TouchableOpacity
          style={styles.footerButton}
          onPress={() => {
            if (isAuthenticated) {
              setChatModalVisible(true);
            } else {
              // Redirect to Login - after login, user will be back to this screen
              navigation.navigate('Login');
            }
          }}
          activeOpacity={0.8}
        >
          <View style={styles.footerButtonContent}>
            <Icon name="comments" size={20} color="#fff" />
            <Text style={styles.footerButtonText}>
              Chat dei tifosi
            </Text>
            {unreadCount > 0 && <View style={styles.footerRedDot} />}
          </View>
        </TouchableOpacity>
      </View>
      <BackToTopButton
        visible={showBackToTop}
        onPress={handleBackToTop}
        bottomOffset={100}
      />

      {/* Chat Modal */}
      <Modal
        visible={chatModalVisible}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={() => setChatModalVisible(false)}
      >
        <View style={[styles.modalContainer, {backgroundColor: isDark ? colors.background : '#f8f9fa'}]}>
          <View style={[styles.modalSafeArea, {paddingTop: insets.top}]}>
            <View style={styles.modalHeader}>
              <View style={styles.modalHeaderTitle}>
              <Icon name="comments" size={20} color="#fff" />
              <Text style={styles.modalTitle}>Chat dei tifosi</Text>
            </View>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setChatModalVisible(false)}
              >
                <Icon name="xmark" size={20} color="#fff" />
              </TouchableOpacity>
            </View>
          </View>
          <ChatTab
            messages={chatMessages}
            matchId={match.match_id}
            onSendMessage={handleSendMessage}
            loading={loading}
            isChatClosed={chatStatus === 'finished'}
            onLoadMore={loadMoreMessages}
            hasMore={hasMoreMessages}
            loadingMore={loadingMore}
          />
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  tabBar: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    zIndex: 10,
  },
  tabsContainer: {
    paddingHorizontal: 16,
  },
  tab: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    marginRight: 8,
    position: 'relative',
  },
  activeTab: {},
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
  },
  activeTabText: {
    color: Colors.primary,
    fontWeight: '600',
  },
  tabIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 16,
    right: 16,
    height: 3,
    backgroundColor: Colors.primary,
    borderRadius: 1.5,
  },
  tabContent: {
    flex: 1,
    minHeight: 400,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  modalSafeArea: {
    backgroundColor: Colors.primary,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: Colors.primary,
  },
  modalHeaderTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  fixedFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 8,
  },
  footerButton: {
    backgroundColor: Colors.primary,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  footerButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  footerButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  footerRedDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#ff4757',
    borderWidth: 1.5,
    borderColor: Colors.primary,
    marginLeft: -4,
    marginTop: -10,
  },
});
